import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";

interface LeadFormProps {
  onSubmit: (data: LeadFormData) => void;
  isSubmitting?: boolean;
}

export interface LeadFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  consent: boolean;
  marketing: boolean;
}

export const LeadForm = ({ onSubmit, isSubmitting = false }: LeadFormProps) => {
  const [formData, setFormData] = useState<LeadFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    consent: false,
    marketing: false
  });
  
  const [errors, setErrors] = useState<Partial<Record<keyof LeadFormData, string>>>({});
  const [formStarted, setFormStarted] = useState(false);
  
  // Track form start
  useEffect(() => {
    if (formStarted && typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', 'LeadFormStarted', {
        form_type: 'Report Lead Form',
        timestamp: new Date().toISOString()
      });
    }
  }, [formStarted]);
  
  const freeEmailProviders = [
    // International providers
    'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
    'yahoo.com', 'yahoo.co.uk',
    'icloud.com', 'aol.com',
    'proton.me', 'protonmail.com', 'zoho.com',
    'gmx.com', 'gmx.net', 'mail.com', 'tutanota.com',
    // Polish providers
    'wp.pl', 'onet.pl', 'onet.eu', 'o2.pl', 'tlen.pl', 'interia.pl', 'interia.eu',
    'vp.pl', 'op.pl', 'autograf.pl', 'buziaczek.pl', 'czateria.pl',
    'poczta.fm', 'spoko.pl', 'amorki.pl', 'adresik.pl', 'gazeta.pl', 't.pl'
  ];
  
  const isBusinessEmail = (email: string): boolean => {
    const domain = email.split('@')[1]?.toLowerCase();
    return domain ? !freeEmailProviders.includes(domain) : false;
  };
  
  const validate = () => {
    const newErrors: Partial<Record<keyof LeadFormData, string>> = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = "Imię jest wymagane";
    if (!formData.lastName.trim()) newErrors.lastName = "Nazwisko jest wymagane";
    if (!formData.email.trim()) {
      newErrors.email = "Email jest wymagany";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Nieprawidłowy adres email";
    } else if (!isBusinessEmail(formData.email)) {
      newErrors.email = "Proszę podać email firmowy";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefon jest wymagany";
    } else if (formData.phone.trim().length < 9) {
      newErrors.phone = "Numer niepoprawny";
    } else if (formData.phone.trim().length > 20) {
      newErrors.phone = "Numer niepoprawny";
    }
    if (!formData.consent) newErrors.consent = "Zgoda jest wymagana";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    } else {
      // Track validation errors
      if (typeof window !== 'undefined' && window.fbq) {
        const errorFields = Object.keys(errors);
        window.fbq('trackCustom', 'LeadFormValidationError', {
          form_type: 'Report Lead Form',
          error_fields: errorFields,
          error_count: errorFields.length,
          has_name_error: !!(errors.firstName || errors.lastName),
          has_email_error: !!errors.email,
          has_phone_error: !!errors.phone,
          has_consent_error: !!errors.consent
        });
      }
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">Imię *</Label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
            onFocus={() => !formStarted && setFormStarted(true)}
            className={errors.firstName ? "border-destructive" : ""}
          />
          {errors.firstName && (
            <p className="text-sm text-destructive">{errors.firstName}</p>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="lastName">Nazwisko *</Label>
          <Input
            id="lastName"
            value={formData.lastName}
            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
            onFocus={() => !formStarted && setFormStarted(true)}
            className={errors.lastName ? "border-destructive" : ""}
          />
          {errors.lastName && (
            <p className="text-sm text-destructive">{errors.lastName}</p>
          )}
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email">Email firmowy *</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          onFocus={() => !formStarted && setFormStarted(true)}
          className={errors.email ? "border-destructive" : ""}
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email}</p>
        )}
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="phone">Telefon *</Label>
        <Input
          id="phone"
          type="tel"
          placeholder="np. +48 123 456 789 (min. 9 znaków)"
          value={formData.phone}
          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
          onFocus={() => !formStarted && setFormStarted(true)}
          className={errors.phone ? "border-destructive" : ""}
          minLength={9}
          maxLength={20}
        />
        {errors.phone && (
          <p className="text-sm text-destructive">{errors.phone}</p>
        )}
      </div>
      
      <div className="space-y-4">
        <div className="flex items-start space-x-2">
          <Checkbox
            id="consent"
            checked={formData.consent}
            onCheckedChange={(checked) => {
              setFormData(prev => ({ ...prev, consent: checked === true }));
              
              // Track consent checkbox interaction
              if (typeof window !== 'undefined' && window.fbq && checked === true) {
                window.fbq('trackCustom', 'ConsentGiven', {
                  consent_type: 'privacy_policy',
                  form_type: 'Report Lead Form'
                });
              }
            }}
          />
          <Label htmlFor="consent" className="text-sm leading-tight cursor-pointer">
            Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z{" "}
            <a href="/polityka-prywatnosci" className="text-primary hover:underline">
              polityką prywatności
            </a>
            . *
          </Label>
        </div>
        {errors.consent && (
          <p className="text-sm text-destructive">{errors.consent}</p>
        )}
        
        <div className="flex items-start space-x-2">
          <Checkbox
            id="marketing"
            checked={formData.marketing}
            onCheckedChange={(checked) => {
              setFormData(prev => ({ ...prev, marketing: checked === true }));
              
              // Track marketing consent
              if (typeof window !== 'undefined' && window.fbq) {
                window.fbq('trackCustom', 'MarketingConsentToggled', {
                  consent_value: checked === true,
                  form_type: 'Report Lead Form'
                });
              }
            }}
          />
          <Label htmlFor="marketing" className="text-sm leading-tight cursor-pointer">
            Chcę otrzymywać informacje marketingowe od LIFactor.com
          </Label>
        </div>
      </div>
      
      <Button 
        type="submit" 
        size="lg" 
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Wysyłanie..." : "Wyślij"}
      </Button>
    </form>
  );
};
